import React, { useState, useMemo } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import { Box } from '@cloudscape-design/components';

// Color palette for tool lines — visually distinct, works on both light and dark backgrounds
const TOOL_COLORS = {
  'Resize': '#2563eb',
  'Background': '#7c3aed',
  'Convert Format': '#059669',
  'Image Link Generator': '#d97706',
  'From IC File': '#0891b2',
  'From Amazon (ASIN)': '#4f46e5',
  'Listing Optimizer': '#be185d',
  'Lead Enrichment': '#16a34a',
};

// Desired display order for the chart legend — matches table group order
const TOOL_ORDER = [
  'Resize',
  'Background',
  'Convert Format',
  'Image Link Generator',
  'From IC File',
  'From Amazon (ASIN)',
  'Listing Optimizer',
  'Lead Enrichment',
];

// Tools excluded from the chart (low activity / deprecated)
const EXCLUDED_TOOLS = new Set(['Lifestyle', 'Product Image Generator']);

const FALLBACK_COLORS = [
  '#2563eb', '#7c3aed', '#059669', '#d97706',
  '#0891b2', '#4f46e5', '#be185d', '#16a34a',
];

function getColor(toolName, index) {
  return TOOL_COLORS[toolName] || FALLBACK_COLORS[index % FALLBACK_COLORS.length];
}

/**
 * Fill in missing dates so the chart shows a continuous 30-day axis
 * even on days with zero runs.
 */
function fillDateGaps(data, toolNames) {
  if (!data || data.length === 0) return [];

  // Determine the 30-day window: last 30 days from today
  const today = new Date();
  const start = new Date(today);
  start.setDate(start.getDate() - 29); // 30 days including today

  const dateMap = new Map();
  for (const row of data) {
    dateMap.set(row.date, row);
  }

  const filled = [];
  const cursor = new Date(start);
  while (cursor <= today) {
    const dateStr = cursor.toISOString().slice(0, 10); // YYYY-MM-DD
    if (dateMap.has(dateStr)) {
      const row = { ...dateMap.get(dateStr) };
      for (const t of toolNames) {
        if (!(t in row)) row[t] = 0;
      }
      filled.push(row);
    } else {
      const emptyRow = { date: dateStr };
      for (const t of toolNames) emptyRow[t] = 0;
      filled.push(emptyRow);
    }
    cursor.setDate(cursor.getDate() + 1);
  }
  return filled;
}

function formatDateTick(dateStr) {
  // "2026-06-25" → "25 Jun"
  const parts = dateStr.split('-');
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${parseInt(parts[2])} ${months[parseInt(parts[1]) - 1]}`;
}

export default function DailyRunsChart({ dailyRuns }) {
  // Discover tool names, filter out excluded, sort by TOOL_ORDER
  const toolNames = useMemo(() => {
    const names = new Set();
    for (const row of (dailyRuns || [])) {
      for (const key of Object.keys(row)) {
        if (key !== 'date' && !EXCLUDED_TOOLS.has(key)) names.add(key);
      }
    }
    // Sort: tools in TOOL_ORDER first (in that order), then any unknown ones at the end
    const ordered = [];
    for (const t of TOOL_ORDER) {
      if (names.has(t)) ordered.push(t);
    }
    for (const t of names) {
      if (!TOOL_ORDER.includes(t)) ordered.push(t);
    }
    return ordered;
  }, [dailyRuns]);

  // Track which tools are visible (all visible by default)
  const [hiddenTools, setHiddenTools] = useState(new Set());

  const chartData = useMemo(() => {
    return fillDateGaps(dailyRuns || [], toolNames);
  }, [dailyRuns, toolNames]);

  const toggleTool = (toolName) => {
    setHiddenTools(prev => {
      const next = new Set(prev);
      if (next.has(toolName)) next.delete(toolName);
      else next.add(toolName);
      return next;
    });
  };

  if (!dailyRuns || dailyRuns.length === 0 || toolNames.length === 0) {
    return null; // No data — don't render the chart
  }

  return (
    <div style={{ marginBottom: '20px' }}>
      <Box variant="h3" margin={{ bottom: 's' }}>Daily Runs (Last 30 Days)</Box>
      <div style={{ width: '100%', height: 280 }}>
        <ResponsiveContainer>
          <LineChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
            <XAxis
              dataKey="date"
              tickFormatter={formatDateTick}
              tick={{ fontSize: 11 }}
              interval="preserveStartEnd"
              minTickGap={40}
            />
            <YAxis
              allowDecimals={false}
              tick={{ fontSize: 11 }}
              width={35}
            />
            <Tooltip
              labelFormatter={(label) => formatDateTick(label)}
              contentStyle={{
                borderRadius: '8px',
                fontSize: '12px',
                border: '1px solid var(--color-border-divider-default, #e9ebed)',
                background: 'var(--color-background-container-content, #fff)',
              }}
            />
            {toolNames.map((tool, i) => (
              <Line
                key={tool}
                type="monotone"
                dataKey={tool}
                stroke={getColor(tool, i)}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4 }}
                hide={hiddenTools.has(tool)}
                connectNulls
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
      {/* Custom legend with clickable pills for select/deselect */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '8px', justifyContent: 'center' }}>
        {toolNames.map((tool, i) => {
          const isHidden = hiddenTools.has(tool);
          const color = getColor(tool, i);
          return (
            <button
              key={tool}
              onClick={() => toggleTool(tool)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '5px',
                padding: '3px 10px',
                borderRadius: '12px',
                border: `1.5px solid ${color}`,
                background: isHidden ? 'transparent' : color,
                color: isHidden ? color : '#fff',
                fontSize: '11px',
                fontWeight: 500,
                cursor: 'pointer',
                opacity: isHidden ? 0.5 : 1,
                transition: 'all 0.15s ease',
              }}
              title={isHidden ? `Show ${tool}` : `Hide ${tool}`}
            >
              <span style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                background: isHidden ? 'transparent' : '#fff',
                border: isHidden ? `2px solid ${color}` : 'none',
                flexShrink: 0,
              }} />
              {tool}
            </button>
          );
        })}
      </div>
    </div>
  );
}
